import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthState, Organization, OrgMembership, User } from '../shared/types';
import { apiPost, apiGet } from '../utils/api';
import { getPublicAppConfig } from '../utils/runtimeConfig';

interface StorageState {
  user: User | null;
  organizations: Organization[];
  orgMemberships: OrgMembership[];
  isDemoSession?: boolean;
}

export interface AuthContextType extends AuthState {
  login: (email: string, password: string, org_id: string, isVerified?: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  setStorage: (storageState: StorageState) => void;
  loginAsDemoUser: () => Promise<boolean>;
  demoEpicPatientId: string;
  demoCernerPatientId: string;
  demoEpicOrgId: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const baseAuthState: AuthState = {
  user: null,
  organizations: [],
  orgMemberships: [],
  isAuthenticated: false,
  isLoading: false,
  isDemoSession: false,
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [ authState, setAuthState ] = useState<AuthState>({ ...baseAuthState, isLoading: true });
  const [ isLoggingOut, setIsLoggingOut ] = useState(false);

  useEffect(() => {
    const loadAuthState = async () => {
      try {
        const response = await apiGet('/api/me');
        if (!response.ok) {
          console.error('Failed to load auth state:', response);
          throw new Error('Failed to load auth state');
        }
        const data = await response.json();
        setAuthState({
          user: data.user,
          organizations: data.organizations || [],
          orgMemberships: data.orgMemberships || [],
          isLoading: false,
          isAuthenticated: true,
          isDemoSession: data.isDemoSession,
        });
      } catch (error) {
        setAuthState(baseAuthState);
      }
    };

    loadAuthState();
  }, []);

  const setStorage = useCallback((storageState: StorageState) => {
    setAuthState({
      user: storageState.user,
      organizations: storageState.organizations,
      orgMemberships: storageState.orgMemberships,
      isLoading: false,
      isAuthenticated: true,
      isDemoSession: storageState.isDemoSession || false,
    });
  }, []);

  const logout = async () => {
    setIsLoggingOut(true);
    setAuthState({ ...authState, isLoading: true });
    try {
      await apiGet('/api/logout');
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setAuthState(baseAuthState);
      setIsLoggingOut(false);
    }
  };

  const login = async (email: string, password: string, orgId: string, isVerified: boolean = false): Promise<boolean> => {
    try {
      const response = await apiPost(`/api/${orgId}/login`, { email, password, isVerified });

      if (response.ok) {
        const data = await response.json();
        const organizations = data.organizations;
        const user = data.user;
        const orgMemberships = data.orgMemberships;

        setStorage({ user, organizations, orgMemberships, isDemoSession: false });
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const loginAsDemoUser = useCallback(async (): Promise<boolean> => {
    try {
      setAuthState({ ...baseAuthState, isLoading: true });
      const response = await apiPost('/api/login-demo', {});
      if (response.ok) {
        const data = await response.json();
        const organizations = data.organizations;
        const user = data.user;
        const orgMemberships = data.orgMemberships;

        setStorage({ user, organizations, orgMemberships, isDemoSession: true });
        return true;
      } else {
        setAuthState(baseAuthState);
        return false;
      }
    } catch (error) {
      setAuthState(baseAuthState);
      return false;
    }
  }, [ setStorage ]);

  const { demoEpicPatientId, demoCernerPatientId, demoEpicOrgId } = getPublicAppConfig();

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    isLoggingOut,
    loginAsDemoUser,
    demoEpicPatientId,
    demoCernerPatientId,
    demoEpicOrgId,
    setStorage,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
